/**
 * ZONIY - Orders Page
 * @module OrdersPage
 * @author Affan - Mobile Developer
 * @description User's order history
 * @version 1.0.0
 * Git: Affan <affan@linesoft.com> - Orders display
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Calendar, DollarSign, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-slate-200 dark:bg-slate-800 w-48" />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 dark:bg-slate-800" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="orders-page">
        <h1 className="font-heading font-black text-4xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          My Orders
        </h1>

        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-20 h-20 text-slate-300 mx-auto mb-6" />
            <h2 className="font-heading font-bold text-2xl text-slate-900 dark:text-white uppercase mb-4">
              No Orders Yet
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              Start shopping to see your orders here.
            </p>
            <Link to="/search">
              <Button className="btn-industrial">
                Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order, index) => (
              <div 
                key={order.id} 
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6"
                data-testid={`order-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-mono text-xs text-slate-500 uppercase tracking-wider">
                      Order #{order.id.slice(0, 8)}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Calendar className="w-4 h-4" />
                        {new Date(order.created_at).toLocaleDateString()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                        order.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {order.payment_status === 'paid' ? 'Paid' : order.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold text-2xl text-blue-600">
                      ${order.total.toFixed(2)}
                    </div>
                    <div className="text-sm text-slate-500">{order.items?.length || 0} items</div>
                  </div>
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <div className="space-y-3">
                    {order.items?.slice(0, 3).map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 flex-shrink-0 flex items-center justify-center overflow-hidden">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="font-mono text-xs text-slate-400">{item.part_number}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 dark:text-white line-clamp-1">
                            {item.name}
                          </div>
                          <div className="text-sm text-slate-500">
                            {item.quantity} × ${item.price.toFixed(2)}
                          </div>
                        </div>
                        <div className="font-heading font-bold text-slate-900 dark:text-white">
                          ${(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="text-sm text-slate-500">
                        +{order.items.length - 3} more items
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrdersPage;
